import { Check, Truck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

type TransportVehicle = {
  id: string;
  name: string;
  vehicleType: string;
};

const VEHICLE_TYPE_ORDER = [
  'DOMESTIC_TRUCK',
  'DOMESTIC_RAIL',
  'INTERNATIONAL_CONTAINER',
];

type Props = {
  allVehicles: TransportVehicle[];
  selectedVehicleIds: string[];
  pendingVehicleIds: Set<string>;
  onToggle: (vehicleId: string, isCurrentlySelected: boolean) => void;
};

export function TransportModesSection({
  allVehicles,
  selectedVehicleIds,
  pendingVehicleIds,
  onToggle,
}: Props) {
  const { t } = useTranslation('settings');

  const grouped = VEHICLE_TYPE_ORDER.map((type) => ({
    type,
    label: t(`settings.facilities.transport_modes.vehicle_types.${type}`),
    vehicles: allVehicles.filter((v) => v.vehicleType === type),
  })).filter((g) => g.vehicles.length > 0);

  return (
    <div className="mt-4 border-t border-neut-100 pt-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Truck size={14} className="text-prim" />
          <span className="text-xs font-semibold text-neut-700">
            {t('settings.facilities.transport_modes.title')}
          </span>
        </div>
        <span className="flex items-center gap-1 text-[10px] text-success-600">
          <Check size={10} />
          {t('settings.facilities.transport_modes.selected', {
            count: selectedVehicleIds.length,
          })}
        </span>
      </div>
      <p className="text-[10px] text-neut-400 mb-3">
        {t('settings.facilities.transport_modes.description')}
      </p>
      <div className="space-y-3">
        {grouped.map((group) => (
          <div key={group.type}>
            <p className="text-[10px] font-semibold text-neut-500 uppercase tracking-wide mb-2">
              {group.label}
            </p>
            <div className="flex flex-wrap gap-2">
              {group.vehicles.map((vehicle) => {
                const isSelected = selectedVehicleIds.includes(vehicle.id);
                const isPending = pendingVehicleIds.has(vehicle.id);
                return (
                  <button
                    key={vehicle.id}
                    type="button"
                    data-testid={`vehicle-chip-${vehicle.id}`}
                    aria-pressed={isSelected}
                    disabled={isPending}
                    onClick={() => onToggle(vehicle.id, isSelected)}
                    className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                      isPending
                        ? 'opacity-50 cursor-not-allowed border-neut-200 bg-neut-bg-alt text-neut-sec'
                        : isSelected
                          ? 'bg-neut-100 border-neut-400 text-neut-800 font-medium'
                          : 'bg-neut-bg-alt border-neut-200 text-neut-sec hover:border-neut-300'
                    }`}
                  >
                    {isSelected && <Check size={10} className="inline mr-1" />}
                    {vehicle.name}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
